import { useState, useEffect } from 'react';
import { FingerprintPattern as Fingerprint, Trash2, Loader as Loader2, TriangleAlert as AlertTriangle } from 'lucide-react';
import {
  isWebAuthnSupported,
  registerBiometric,
  getUserCredentials,
  removeCredential,
  getWebAuthnErrorMessage,
} from '@/lib/webauthn';

interface Credential {
  id: string;
  credential_id: string;
  device_name: string;
  created_at: string;
}

export default function BiometricSettingsSection() {
  const [supported, setSupported] = useState(true);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setSupported(isWebAuthnSupported());
    loadCredentials();
  }, []);

  async function loadCredentials() {
    setLoading(true);
    try {
      const data = await getUserCredentials();
      setCredentials(data);
    } catch {
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister() {
    setRegistering(true);
    setError('');
    try {
      await registerBiometric();
      await loadCredentials();
    } catch (err: unknown) {
      setError(getWebAuthnErrorMessage(err));
    } finally {
      setRegistering(false);
    }
  }

  async function handleRemove(id: string) {
    setRemoving(id);
    try {
      await removeCredential(id);
      setCredentials((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setError('Failed to remove credential.');
    } finally {
      setRemoving(null);
    }
  }

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-2.5 px-3">
        <Loader2 size={15} className="animate-spin text-white" />
        <span className="text-xs text-white">Checking biometric status...</span>
      </div>
    );
  }

  if (!supported) {
    return (
      <div className="flex items-center gap-3 py-2.5 px-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={15} className="text-amber-500" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-semibold text-white">Biometric Login</p>
          <p className="text-[11px] text-white">Not supported by this browser</p>
        </div>
      </div>
    );
  }

  const hasCredentials = credentials.length > 0;

  return (
    <div className="py-1">
      <div className="flex items-center gap-3 py-2.5 px-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${hasCredentials ? 'bg-teal-500/10' : 'bg-white/10'}`}>
          <Fingerprint size={15} className={hasCredentials ? 'text-teal-400' : 'text-white'} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Biometric Login</p>
          <p className="text-[11px] text-white">
            {hasCredentials
              ? `${credentials.length} passkey${credentials.length !== 1 ? 's' : ''} registered`
              : 'Use fingerprint or face to sign in'}
          </p>
        </div>
        <div className={`px-2 py-1 rounded-lg text-[10px] font-semibold ${hasCredentials ? 'bg-teal-500/10 text-teal-400' : 'bg-white/10 text-white'}`}>
          {hasCredentials ? 'ON' : 'OFF'}
        </div>
      </div>

      {error && (
        <div className="mx-3 mb-2 p-2.5 bg-red-500/10 border border-red-200 rounded-xl">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {hasCredentials && (
        <div className="mx-3 mt-1 mb-2 space-y-1.5">
          {credentials.map((cred) => (
            <div
              key={cred.id}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl glass-inner"
            >
              <Fingerprint size={13} className="text-teal-400" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{cred.device_name}</p>
                <p className="text-[10px] text-white">Added {formatDate(cred.created_at)}</p>
              </div>
              <button
                onClick={() => handleRemove(cred.id)}
                disabled={removing === cred.id}
                className="p-1.5 glass-hover-red rounded-lg text-white hover:text-red-500"
              >
                {removing === cred.id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Trash2 size={12} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mx-3 mb-2">
        <button
          onClick={handleRegister}
          disabled={registering}
          className="w-full py-2 text-[11px] font-semibold text-teal-400 glass-hover-teal rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50 border border-teal-200"
        >
          {registering ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            <Fingerprint size={11} />
          )}
          {hasCredentials ? 'Add Another Passkey' : 'Enable Biometric Login'}
        </button>
      </div>
    </div>
  );
}
